import { createBrowserRouter } from "react-router";
import { portalBasename } from "@shared-core/routing/basename";
import { routes } from "@shared-core/routing/routes";
import {
  PortalLayout,
  type PortalRouteHandle,
} from "../pages/portal/PortalLayout";
import { PortalSignInPage } from "../pages/portal/PortalSignInPage";
import { PortalWelcomePage } from "../pages/portal/signin/PortalWelcomePage";
import { PortalChooseCustomerPage } from "../pages/portal/signin/PortalChooseCustomerPage";
import { PortalNoAccessPage } from "../pages/portal/signin/PortalNoAccessPage";
import { PortalCustomerBlockedPage } from "../pages/portal/signin/PortalCustomerBlockedPage";
import { PortalOrdersPage } from "../pages/portal/PortalOrdersPage";
import { PortalOrderRoute } from "../pages/portal/PortalOrderRoute";
import { PortalQuoteDetailPage } from "../pages/portal/PortalQuoteDetailPage";
import { PortalOrderLinesRedirect } from "../pages/portal/PortalOrderLinesRedirect";
import { PortalQuotesPage } from "../pages/portal/PortalQuotesPage";
import { PortalSpendPage } from "../pages/portal/PortalSpendPage";
import { PortalArtworkCheckPage } from "../pages/portal/PortalArtworkCheckPage";
import { PortalArtworkCheckResultPage } from "../pages/portal/PortalArtworkCheckResultPage";
import { PortalCataloguePage } from "../pages/portal/PortalCataloguePage";
import { PortalCatalogueItemPage } from "../pages/portal/PortalCatalogueItemPage";
import { PortalBasketPage } from "../pages/portal/basket/PortalBasketPage";
import { PortalCheckoutPage } from "../pages/portal/basket/PortalCheckoutPage";
import { PortalOrderPlacedRedirect } from "../pages/portal/basket/PortalOrderPlacedRedirect";
import { QuoteLinkPage } from "../pages/customer-pages/QuoteLinkPage";
import { OrderLinkPage } from "../pages/customer-pages/OrderLinkPage";
import { ProofLinkPage } from "../pages/customer-pages/ProofLinkPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { PortalHomePage } from "../pages/portal/PortalHomePage";
import { previewSignInRoutes } from "../pages/preview-sign-in/routes";

// Customer portal routing — the whole app IS the portal, so there is no
// dashboard shell to opt out of and no feature gate to check (unlike the
// staff app's routes.tsx, which the portal pages used to live inside).
//
// Sign-in sits outside PortalLayout because it has no nav (you are not
// signed in yet) — same shape as before the move, minus the dashboard
// wrapper. Route paths lost their `/portal/...` prefix as part of the move
// to a standalone shell on its own hostname (`orders.wearepollards.com`):
// a `/portal` segment was namespacing against the staff app's route table,
// which no longer applies once the portal owns its whole origin — see
// `packages/shared-core/src/routing/routes.ts`. `/` is the customer's branded
// home page (docs/features/portal-theme), or the orders list where the theme
// read failed or the feature is off (PortalHomePage redirects).
const routeConfig = [
  {
    path: routes.portalSignIn.path,
    Component: PortalSignInPage,
  },
  // What the sign-in callback decided once Entra verified the email: first
  // welcome, the customer chooser, turned away, a blocked customer. Outside
  // PortalLayout for the same reason as sign-in: no customer chosen yet.
  { path: routes.portalWelcome.path, Component: PortalWelcomePage },
  {
    path: routes.portalChooseCustomer.path,
    Component: PortalChooseCustomerPage,
  },
  { path: routes.portalNoAccess.path, Component: PortalNoAccessPage },
  {
    path: routes.portalCustomerBlocked.path,
    Component: PortalCustomerBlockedPage,
  },
  // The customer's quote, order and per-proof pages by their emailed link: no sign-in
  // (the token in the path is the credential), so outside PortalLayout,
  // whose session fetch would bounce a link holder to /sign-in.
  { path: routes.portalQuoteLink.path, Component: QuoteLinkPage },
  { path: routes.portalOrderLink.path, Component: OrderLinkPage },
  { path: routes.portalProofLink.path, Component: ProofLinkPage },
  {
    Component: PortalLayout,
    children: [
      // routes.portalHome: the branded home (layout B), as wide as the order and quote pages.
      {
        index: true,
        Component: PortalHomePage,
        handle: { width: "6xl" } satisfies PortalRouteHandle,
      },
      { path: routes.portalOrders.path, Component: PortalOrdersPage },
      {
        path: routes.portalOrderLines.path,
        Component: PortalOrderLinesRedirect,
      },
      // One address, two pages (PortalOrderRoute). Both are laid out as the quote page is: the order's content with
      // its money summary beside it from `lg` up (max-w-6xl).
      {
        path: routes.portalOrderDetail.path,
        Component: PortalOrderRoute,
        handle: { width: "6xl" } satisfies PortalRouteHandle,
      },
      { path: routes.portalQuotes.path, Component: PortalQuotesPage },
      // The quote page has a decision panel beside its content from `lg` up, as the emailed link's does (max-w-6xl).
      {
        path: routes.portalQuoteDetail.path,
        Component: PortalQuoteDetailPage,
        handle: { width: "6xl" } satisfies PortalRouteHandle,
      },
      { path: routes.portalSpend.path, Component: PortalSpendPage },
      {
        path: routes.portalArtworkCheck.path,
        Component: PortalArtworkCheckPage,
      },
      {
        path: routes.portalArtworkCheckResult.path,
        Component: PortalArtworkCheckResultPage,
      },
      { path: routes.portalCatalogue.path, Component: PortalCataloguePage },
      {
        path: routes.portalCatalogueItem.path,
        Component: PortalCatalogueItemPage,
        handle: { wide: true } satisfies PortalRouteHandle,
      },
      // The basket and the checkout keep a summary beside them from `lg` up, like the quote page.
      {
        path: routes.portalBasket.path,
        Component: PortalBasketPage,
        handle: { width: "6xl" } satisfies PortalRouteHandle,
      },
      {
        path: routes.portalCheckout.path,
        Component: PortalCheckoutPage,
        handle: { width: "6xl" } satisfies PortalRouteHandle,
      },
      {
        path: routes.portalOrderPlaced.path,
        Component: PortalOrderPlacedRedirect,
      },
    ],
  },
  // The new sign-in, clickable in mock mode, beside the pages it will replace (docs/features/portal-sign-in).
  ...previewSignInRoutes,
  { path: "*", Component: NotFoundPage },
];

export const router = createBrowserRouter(routeConfig, {
  basename: portalBasename(import.meta.env?.BASE_URL),
});
